<template>
	<div>
		<div class="vx-card p-6">
			<!-- Header with Back Button -->
			<div class="flex items-center justify-between mb-6">
				<div class="flex items-center">
					<vs-button
						size="large"
						icon-pack="feather"
						icon="icon-arrow-left"
						type="line"
						@click="goBack()"
						class="mr-4"
					>
					</vs-button>
					<div>
						<h2 class="mb-1">{{ listDetails.name || "Liste de Backlinks" }}</h2>
						<div class="flex items-center text-gray-600">
							<span v-if="listDetails.society_name" class="mr-4">
								<strong>Société:</strong> {{ listDetails.society_name }}
							</span>
							<span v-if="listDetails.created_date">
								<strong>Créé le:</strong>
								{{ formatDate(listDetails.created_date) }}
							</span>
						</div>
					</div>
				</div>

				<div class="flex items-center">
					<!-- Export Button -->
					<vs-button
						color="primary"
						type="filled"
						icon-pack="feather"
						icon="icon-download"
						class="mr-3"
						@click="exportToXLSX()"
					>
						Exporter
					</vs-button>
					<!-- Delete Button -->
					<vs-button
						color="danger"
						type="filled"
						icon-pack="feather"
						icon="icon-trash-2"
						@click="confirmDelete()"
					>
					</vs-button>
				</div>
			</div>

			<!-- Summary Card -->
			<!-- <div class="summary-card mb-6 p-4 bg-gray-100 rounded-lg">
				<div class="flex items-center justify-between">
					<div>
						<p class="text-sm text-gray-600 mb-1">Nombre total de backlinks</p>
						<p class="text-2xl font-bold text-primary">{{ backlinks.length }}</p>
					</div>
					<div class="text-right">
						<p class="text-sm text-gray-600 mb-1">Prix total estimé</p>
						<p class="text-xl font-semibold">{{ calculateTotalPrice() }} €</p>
					</div>
				</div>
			</div> -->

			<!-- Loading State -->
			<div v-if="loading" class="text-center py-6">
				<vs-loading active />
			</div>

			<!-- Backlinks Table -->
			<div v-else class="table-scroll-wrapper">
			<vs-table
				:data="backlinks"
				stripe
				pagination
				max-items="20"
				noDataText="Aucun backlink disponible"
			>
				<template slot="thead">
					<vs-th>Site</vs-th>
					<vs-th>Pays</vs-th>
					<vs-th style="text-align: right">Meilleur Prix</vs-th>
					<vs-th>Meilleure Plateforme</vs-th>
					<vs-th style="text-align: right">Paperclub</vs-th>
					<vs-th style="text-align: right">Rocketlinks</vs-th>
					<vs-th style="text-align: right">Getfluence</vs-th>
					<vs-th style="text-align: right">Ereferer</vs-th>
					<vs-th style="text-align: right">Bazoom</vs-th>
					<vs-th style="text-align: right">Trafic</vs-th>
					<vs-th style="text-align: right">TF</vs-th>
					<vs-th style="text-align: right">CF</vs-th>
					<vs-th style="text-align: right">Domaines Ref</vs-th>
					<vs-th style="text-align: right">Keywords</vs-th>
					<vs-th style="text-align: right">Score BQS</vs-th>
					<vs-th>Catégories</vs-th>
				</template>

				<template slot-scope="{ data }">
					<vs-tr v-for="(backlink, index) in data" :key="index">
						<vs-td>
							<a
								:href="`https://${backlink.name}`"
								target="_blank"
								class="text-primary"
							>
								{{ backlink.name }}
							</a>
						</vs-td>
						<vs-td>{{ getMetaField(backlink, 'hosting_country') || "-" }}</vs-td>
						<vs-td style="text-align: right">
							{{
								getBestPrice(backlink) !== null
									? formatNumberWithSpaces(getBestPrice(backlink)) + " €"
									: "-"
							}}
						</vs-td>
						<vs-td style="text-align: right">
							<a
								v-if="getBestPriceUrl(backlink)"
								:href="getBestPriceUrl(backlink)"
								target="_blank"
								class="text-primary"
							>
								{{ capitalize(getBestPricePlatform(backlink)) }}
							</a>
							<span v-else>{{ capitalize(getBestPricePlatform(backlink)) || "-" }}</span>
						</vs-td>
						<vs-td style="text-align: right">
							<a
								v-if="getProviderPrice(backlink, 'paperclub') > 0 && getProviderUrl(backlink, 'paperclub')"
								:href="getProviderUrl(backlink, 'paperclub')"
								target="_blank"
								class="text-primary"
							>
								{{ formatNumberWithSpaces(getProviderPrice(backlink, 'paperclub')) }} €
							</a>
							<span v-else-if="getProviderPrice(backlink, 'paperclub') > 0">
								{{ formatNumberWithSpaces(getProviderPrice(backlink, 'paperclub')) }} €
							</span>
							<span v-else>-</span>
						</vs-td>
						<vs-td style="text-align: right">
							<a
								v-if="getProviderPrice(backlink, 'rocketlinks') > 0 && getProviderUrl(backlink, 'rocketlinks')"
								:href="getProviderUrl(backlink, 'rocketlinks')"
								target="_blank"
								class="text-primary"
							>
								{{ formatNumberWithSpaces(getProviderPrice(backlink, 'rocketlinks')) }} €
							</a>
							<span v-else-if="getProviderPrice(backlink, 'rocketlinks') > 0">
								{{ formatNumberWithSpaces(getProviderPrice(backlink, 'rocketlinks')) }} €
							</span>
							<span v-else>-</span>
						</vs-td>
						<vs-td style="text-align: right">
							<a
								v-if="getProviderPrice(backlink, 'getfluence') > 0 && getProviderUrl(backlink, 'getfluence')"
								:href="getProviderUrl(backlink, 'getfluence')"
								target="_blank"
								class="text-primary"
							>
								{{ formatNumberWithSpaces(getProviderPrice(backlink, 'getfluence')) }} €
							</a>
							<span v-else-if="getProviderPrice(backlink, 'getfluence') > 0">
								{{ formatNumberWithSpaces(getProviderPrice(backlink, 'getfluence')) }} €
							</span>
							<span v-else>-</span>
						</vs-td>
						<vs-td style="text-align: right">
							<a
								v-if="getProviderPrice(backlink, 'ereferer') > 0 && getProviderUrl(backlink, 'ereferer')"
								:href="getProviderUrl(backlink, 'ereferer')"
								target="_blank"
								class="text-primary"
							>
								{{ formatNumberWithSpaces(getProviderPrice(backlink, 'ereferer')) }} €
							</a>
							<span v-else-if="getProviderPrice(backlink, 'ereferer') > 0">
								{{ formatNumberWithSpaces(getProviderPrice(backlink, 'ereferer')) }} €
							</span>
							<span v-else>-</span>
						</vs-td>
						<vs-td style="text-align: right">
							<a
								v-if="getProviderPrice(backlink, 'bazoom') > 0 && getProviderUrl(backlink, 'bazoom')"
								:href="getProviderUrl(backlink, 'bazoom')"
								target="_blank"
								class="text-primary"
							>
								{{ formatNumberWithSpaces(getProviderPrice(backlink, 'bazoom')) }} €
							</a>
							<span v-else-if="getProviderPrice(backlink, 'bazoom') > 0">
								{{ formatNumberWithSpaces(getProviderPrice(backlink, 'bazoom')) }} €
							</span>
							<span v-else>-</span>
						</vs-td>
						<vs-td style="text-align: right">{{
							formatNumberWithSpaces(getMetaField(backlink, 'traffic'))
						}}</vs-td>
						<vs-td style="text-align: right">{{ getMetaField(backlink, 'tf') || "-" }}</vs-td>
						<vs-td style="text-align: right">{{ getMetaField(backlink, 'cf') || "-" }}</vs-td>
						<vs-td style="text-align: right">{{
							formatNumberWithSpaces(getMetaField(backlink, 'rd'))
						}}</vs-td>
						<vs-td style="text-align: right">{{
							formatNumberWithSpaces(getMetaField(backlink, 'keywords'))
						}}</vs-td>
						<vs-td style="text-align: right">
							<vs-chip
								v-if="
									backlink.bqs_score !== null &&
									backlink.bqs_score !== undefined
								"
								:color="getBqsColor(backlink.bqs_score)"
								style="font-size: 12px"
							>
								{{ Math.round(Number(backlink.bqs_score)) }}
							</vs-chip>
							<span v-else>-</span>
						</vs-td>
						<vs-td>
							<div v-if="backlink.category" class="category-chips">
								<vs-chip
									v-for="(cat, catIndex) in getCategoryArray(backlink.category)"
									:key="catIndex"
									color="primary"
									style="margin: 2px; font-size: 11px"
								>
									{{ cat.trim() }}
								</vs-chip>
							</div>
							<span v-else>-</span>
						</vs-td>
					</vs-tr>
				</template>
			</vs-table>
			</div>
		</div>

		<!-- Delete Confirmation Popup -->
		<DeletePopup
			:show="showDeletePopup"
			@close-popup="showDeletePopup = false"
			@oui="deleteList()"
		/>
	</div>
</template>

<script>
import { Backlinks } from "@/entities/Backlinks";
import utilsMixin from "@/mixins/utilsMixin";
import moment from "moment";
import XLSX from "xlsx";
import DeletePopup from "@/components/popups/DeletePopup.vue";

export default {
	name: "BacklinksDetails",
	mixins: [utilsMixin],
	components: {
		DeletePopup,
	},
	data() {
		return {
			listDetails: {},
			backlinks: [],
			loading: false,
			showDeletePopup: false,
		};
	},
	mounted() {
		this.fetchListDetails();
	},
	methods: {
		async fetchListDetails() {
			try {
				this.loading = true;
				const listId = this.$route.params.id;

				// Use getListDetails which calls /backlinks/list/:id/full API
				const response = await Backlinks.getListDetails(listId);

				if (response.success && response.data) {
					this.listDetails = response.data;
					this.backlinks = response.data.items || response.data.backlinks || [];
				} else {
					this.$vs.notify({
						title: "Erreur",
						text: "Liste introuvable",
						color: "danger",
						position: "top-right",
					});
					this.goBack();
				}
			} catch (error) {
				console.error("Error fetching list details:", error);
				this.$vs.notify({
					title: "Erreur",
					text: "Impossible de charger les détails de la liste",
					color: "danger",
					position: "top-right",
				});
				this.goBack();
			} finally {
				this.loading = false;
			}
		},

		goBack() {
			this.$router.push({ name: "backlinks-lists" });
		},

		formatDate(date) {
			return date ? moment(date).format("DD/MM/YYYY") : "-";
		},

		formatNumberWithSpaces(num) {
			if (!num && num !== 0) return "0";
			return new Intl.NumberFormat("fr-FR").format(num);
		},

		getBqsColor(score) {
			const value = 101 - Number(score);
			if (value === 0 || isNaN(value)) return "#C3C3C3";
			return this.gradientColor(value);
		},

		getCategoryArray(categoryString) {
			if (!categoryString) return [];
			return categoryString.split(",").filter((cat) => cat && cat.trim());
		},

		getMetaField(backlink, field) {
			const keyMap = {
				traffic: "Traffic",
				tf: "TF",
				cf: "CF",
				rd: "RD",
				dr: "DR",
				ar: "AR",
				ur: "UR",
				keywords: "Top 3 Kws",
				backlinks: "Backlinks",
				ref_ips: "Ref ips",
				ref_subnets: "Ref Subnets",
				ref_edu: "Ref Edu",
				ref_gov: "Ref Gov",
				traffic_value: "Traffic Value",
				hosting_country: "hosting_country",
				hosting_code: "hosting_code",
				domain: "Domain",
				ttf: "TTF",
			};
			const mappedKey = keyMap[field] || field;
			if (backlink.meta_data) {
				if (backlink.meta_data[mappedKey] !== undefined) {
					return backlink.meta_data[mappedKey];
				}
				if (backlink.meta_data[field] !== undefined) {
					return backlink.meta_data[field];
				}
			}
			return backlink[field];
		},

		getProviderPrice(backlink, provider) {
			if (backlink.meta_data && backlink.meta_data[provider]) {
				const price = Number(backlink.meta_data[provider]);
				return price > 0 ? price : 0;
			}
			return 0;
		},

		getProviderUrl(backlink, provider) {
			if (backlink.meta_data) {
				return backlink.meta_data[provider + "_url"] || null;
			}
			return null;
		},

		getBestPricePlatform(backlink) {
			if (backlink.meta_data && backlink.meta_data.best_price) {
				return backlink.meta_data.best_price;
			}
			return null;
		},

		capitalize(str) {
			if (!str) return str;
			return str.charAt(0).toUpperCase() + str.slice(1);
		},

		getBestPriceUrl(backlink) {
			if (backlink.meta_data && backlink.meta_data.best_price) {
				const platform = backlink.meta_data.best_price;
				const urlKey = platform + "_url";
				if (backlink.meta_data[urlKey]) {
					return backlink.meta_data[urlKey];
				}
			}
			return null;
		},

		getBestPrice(backlink) {
			if (backlink.meta_data && backlink.meta_data.best_price) {
				const platform = backlink.meta_data.best_price;
				const price = backlink.meta_data[platform];
				if (price && price > 0) return price;
			}
			if (backlink.meta_data && backlink.meta_data["Meilleur prix"]) {
				return backlink.meta_data["Meilleur prix"];
			}
			return null;
		},

		calculateTotalPrice() {
			const total = this.backlinks.reduce((sum, backlink) => {
				const price = this.getBestPrice(backlink) || 0;
				return sum + parseFloat(price);
			}, 0);
			return this.formatNumberWithSpaces(total);
		},

		exportToXLSX() {
			if (!this.backlinks || this.backlinks.length === 0) return;

			const providers = ['paperclub', 'rocketlinks', 'getfluence', 'ereferer', 'bazoom'];

			const data = this.backlinks.map(b => {
				const bestPrice = this.getBestPrice(b);
				return {
					'Site': b.name || '',
					'Pays': this.getMetaField(b, 'hosting_country') || '',
					'Meilleur Prix': bestPrice !== null ? bestPrice : '',
					'Meilleure Plateforme': this.getBestPricePlatform(b) || '',
					'Paperclub': this.getProviderPrice(b, 'paperclub') > 0 ? this.getProviderPrice(b, 'paperclub') : '',
					'Rocketlinks': this.getProviderPrice(b, 'rocketlinks') > 0 ? this.getProviderPrice(b, 'rocketlinks') : '',
					'Getfluence': this.getProviderPrice(b, 'getfluence') > 0 ? this.getProviderPrice(b, 'getfluence') : '',
					'Ereferer': this.getProviderPrice(b, 'ereferer') > 0 ? this.getProviderPrice(b, 'ereferer') : '',
					'Bazoom': this.getProviderPrice(b, 'bazoom') > 0 ? this.getProviderPrice(b, 'bazoom') : '',
					'Trafic': this.getMetaField(b, 'traffic') || 0,
					'TF': this.getMetaField(b, 'tf') || '',
					'CF': this.getMetaField(b, 'cf') || '',
					'Domaines Ref': this.getMetaField(b, 'rd') || 0,
					'Keywords': this.getMetaField(b, 'keywords') || 0,
					'Score BQS': b.bqs_score != null ? Math.round(Number(b.bqs_score)) : '',
					'Catégories': b.category || '',
				};
			});

			const wb = XLSX.utils.book_new();
			const ws = XLSX.utils.json_to_sheet(data);
			XLSX.utils.book_append_sheet(wb, ws, 'Backlinks');
			XLSX.writeFile(wb, (this.listDetails.name || 'backlinks') + '.xlsx');
		},

		confirmDelete() {
			this.showDeletePopup = true;
		},

		async deleteList() {
			try {
				this.showDeletePopup = false;

				await Backlinks.deleteList(this.listDetails.id);

				this.$vs.notify({
					title: "Succès",
					text: "Liste supprimée avec succès",
					color: "success",
					position: "top-right",
				});

				// Navigate back to the lists page
				this.goBack();
			} catch (error) {
				console.error("Error deleting list:", error);
				this.$vs.notify({
					title: "Erreur",
					text: "Impossible de supprimer la liste",
					color: "danger",
					position: "top-right",
				});
			}
		},
	},
};
</script>

<style scoped>
.text-primary {
	color: #7367f0;
	text-decoration: none;
}

.text-primary:hover {
	text-decoration: underline;
}

.category-chips {
	display: flex;
	flex-wrap: wrap;
	gap: 4px;
	align-items: center;
}

.summary-card {
	border: 1px solid #e0e6ed;
}

.table-scroll-wrapper {
	overflow-x: auto;
	width: 100%;
}
</style>